import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, rufe, ApiFehler } from "../api/client";
import { istNetzfehler, offlinePreisMerken } from "./offline";
import type { ArtikelZeile, regaleDesMieters } from "../../worker/data/artikel";
import type { ArtikelAendern, ArtikelAnlegen, PreisSenken } from "@shared/schemas/artikel";

export type { ArtikelZeile, ArtikelAnlegen, ArtikelAendern };
export type MieterRegal = Awaited<ReturnType<typeof regaleDesMieters>>[number];
export type ArtikelStatus = ArtikelZeile["status"];

export interface ErfassenInfo {
  regale: MieterRegal[];
  preisChipsCent: number[];
  verboteneWaren?: string[];
  kategorieGrenzen?: { kategorie: string; minCent: number; maxCent: number }[];
  preisMinCent: number;
  preisMaxCent: number;
  heuteErfasst: number;
}

const K = ["mieter", "artikel"] as const;

function nachAenderung() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: K });
    void qc.invalidateQueries({ queryKey: ["mieter", "erfassen"] });
    void qc.invalidateQueries({ queryKey: ["mieter", "start"] });
  };
}

export const useErfassenInfo = () => useQuery({ queryKey: ["mieter", "erfassen"], queryFn: () => rufe<ErfassenInfo>(api.api.mieter.erfassen.$get()) });
export const useMeineArtikel = (status?: ArtikelStatus, q?: string, nur?: "ohne_foto" | "lagerhueter" | "packliste" | "druckliste") =>
  useQuery({ queryKey: [...K, status ?? "", q ?? "", nur ?? ""], queryFn: () => rufe<{ artikel: ArtikelZeile[] }>(api.api.mieter.artikel.$get({ query: { status, q: q || undefined, nur } })), placeholderData: (alt) => alt });
export const useMeinArtikel = (id: string) => useQuery({ queryKey: [...K, id], queryFn: () => rufe<{ artikel: ArtikelZeile; preisverlauf: { altCent: number; neuCent: number; grund: string; erstelltAt: string }[] }>(api.api.mieter.artikel[":id"].$get({ param: { id } })), enabled: !!id });

export function useArtikelAnlegen() {
  const nach = nachAenderung();
  return useMutation({ mutationFn: (d: ArtikelAnlegen) => rufe<{ artikel: ArtikelZeile[] }>(api.api.mieter.artikel.$post({ json: d })), onSuccess: nach });
}
export function useArtikelAendern() {
  const nach = nachAenderung();
  return useMutation({ mutationFn: (v: { id: string } & ArtikelAendern) => rufe<{ artikel: ArtikelZeile }>(api.api.mieter.artikel[":id"].$put({ param: { id: v.id }, json: v })), onSuccess: nach });
}
export function usePreisSenken() {
  const nach = nachAenderung();
  return useMutation({
    mutationFn: async (d: PreisSenken) => {
      try {
        return await rufe<{ geaendert: number }>(api.api.mieter.artikel["preis-senken"].$post({ json: d }));
      } catch (e) {
        // E12.3: ohne Netz vormerken, wird beim nächsten Online-Gehen gesendet
        if (!(e instanceof ApiFehler) && istNetzfehler(e) && (await offlinePreisMerken(d))) return { geaendert: 0, offline: true as const };
        throw e;
      }
    },
    onSuccess: nach,
  });
}
export function useArtikelStatus() {
  const nach = nachAenderung();
  return useMutation({ mutationFn: (v: { id: string; status: "abgeholt" | "entfernt" | "im_regal" }) => rufe(api.api.mieter.artikel[":id"].status.$post({ param: { id: v.id }, json: { status: v.status } })), onSuccess: nach });
}
export function useFotoHochladen() {
  const nach = nachAenderung();
  return useMutation({
    mutationFn: async (v: { id: string; blob: Blob }) => {
      const res = await fetch(`/api/mieter/artikel/${v.id}/foto`, { method: "PUT", body: v.blob, headers: { "content-type": v.blob.type || "image/jpeg" }, credentials: "same-origin" });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { fehler?: string };
        throw new ApiFehler(body.fehler ?? "Foto konnte nicht gespeichert werden.", res.status);
      }
      return (await res.json()) as { fotoKey: string };
    },
    onSuccess: nach,
  });
}
export function useFotoLoeschen() {
  const nach = nachAenderung();
  return useMutation({ mutationFn: (id: string) => rufe(api.api.mieter.artikel[":id"].foto.$delete({ param: { id } })), onSuccess: nach });
}

export function fotoUrl(key: string | null): string | undefined {
  return key ? `/api/mieter/dateien/${key}` : undefined;
}

/** Etiketten-PDF holen und öffnen (Druckdialog des Browsers). */
export async function etikettenOeffnen(artikelIds: string[], format: "62x29" | "a4", startfeld = 1): Promise<void> {
  const res = await api.api.mieter.etiketten.$post({ json: { artikelIds, format, startfeld } });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { fehler?: string };
    throw new Error(body.fehler ?? "Etiketten konnten nicht erzeugt werden.");
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const fenster = window.open(url, "_blank");
  if (!fenster) {
    const a = document.createElement("a");
    a.href = url;
    a.download = `etiketten-${format}.pdf`;
    a.click();
  }
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}
